import DataLoader from 'app/ui_v2/components/data-loader/DataLoader';
import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router';

// Lazy load guards and layouts
const CheckIsLoggedInGuard = lazy(() => import('app/core/guards/CheckIsLoggedInGuard'));
const HasToBeLoggedInGuard = lazy(() => import('app/core/guards/HasToBeLoggedInGuard'));
const CannotBeLoggedInGuard = lazy(() =>
  import('../../guards/CannotBeLoggedInGuard').then((m) => ({ default: m.CannotBeLoggedInGuard })),
);
const NoSidebar = lazy(() => import('../../layout/NoSidebar'));

// Lazy load page components
const RegisterPage = lazy(() => import('../../../main/register-page/RegisterPage'));
const Dashboard = lazy(() => import('../../../main/dashboard/Dashboard'));
const NotificationsHome = lazy(() => import('../../../main/common/messaging/NotificationsHome'));
const ViewReportPage = lazy(() => import('../../../main/common/reports/ViewReportPage'));
const TicketsRedirectPage = lazy(() => import('../../../main/common/tickets/TicketsRedirectPage'));
const ResendEmailPage = lazy(() => import('../../../main/resend-email/ResendEmailPage'));
const Login = lazy(() => import('../../../main/auth/Login'));
const LoginAs = lazy(() => import('../../../main/auth/login-as/LoginAs'));
const LoginOauth = lazy(() => import('../../../main/auth/login-oauth/LoginOauth'));
const Logout = lazy(() => import('../../../main/auth/Logout'));
const LoggedOut = lazy(() => import('../../../main/auth/LoggedOut'));
const AccelaStatus = lazy(() => import('../../../main/accela/AccelaStatus'));
const Error404Page = lazy(() => import('../../../main/errors/Error404Page'));
const AccountTypePicker = lazy(() => import('../../../main/agency/onboarding/account-type-picker/AccountTypePicker'));
const AgenciesListPage = lazy(() => import('../../../main/common/agencies-list/AgenciesListPage'));

const commonRoutes: RouteObject[] = [
  {
    path: '/dashboard',
    element: (
      <Suspense fallback={<DataLoader />}>
        <HasToBeLoggedInGuard>
          <Dashboard />
        </HasToBeLoggedInGuard>
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <CheckIsLoggedInGuard>
        <NoSidebar />
      </CheckIsLoggedInGuard>
    ),
    errorElement: <Error404Page />,
    children: [
      {
        path: '/register',
        element: (
          <CannotBeLoggedInGuard>
            <RegisterPage />
          </CannotBeLoggedInGuard>
        ),
      },
      {
        path: '/pick-account',
        element: (
          <Suspense fallback={<DataLoader />}>
            <HasToBeLoggedInGuard>
              <AccountTypePicker />
            </HasToBeLoggedInGuard>
          </Suspense>
        ),
      },
      {
        path: '/agencies-list',
        element: (
          <Suspense fallback={<DataLoader />}>
            <AgenciesListPage />
          </Suspense>
        ),
      },
      // MESSAGES REDIRECTS
      {
        path: '/n/:messageId',
        element: <NotificationsHome />,
      },
      {
        path: '/l/:messageId',
        element: (
          <HasToBeLoggedInGuard>
            <NotificationsHome />
          </HasToBeLoggedInGuard>
        ),
      },
      // REPORTS REDIRECTS
      {
        path: '/r/:reportId',
        element: <ViewReportPage />,
      },
      // TICKETS REDIRECTS
      {
        path: '/t/:ticketId/:section',
        element: (
          <HasToBeLoggedInGuard>
            <TicketsRedirectPage />
          </HasToBeLoggedInGuard>
        ),
      },
      // OTHER
      {
        path: '/resend-email',
        element: (
          <CannotBeLoggedInGuard>
            <ResendEmailPage />
          </CannotBeLoggedInGuard>
        ),
      },
      {
        path: '/auth/login',
        element: (
          <CannotBeLoggedInGuard>
            <Login />
          </CannotBeLoggedInGuard>
        ),
      },
      {
        path: '/auth/login-as/:id',
        element: <LoginAs />,
      },
      {
        path: '/auth/login-oauth',
        element: <LoginOauth />,
      },
      {
        path: '/auth/logout',
        element: <Logout />,
      },
      {
        path: '/auth/logged-out',
        element: <LoggedOut />,
      },
      {
        path: '/accela/status',
        element: <AccelaStatus />,
      },
    ],
  },
  // unauthorized routes
  {
    path: '/auth',
    element: <Navigate to={'/auth/login'} />,
  },

  // main route redirect to auth
  {
    path: '/',
    element: <Navigate to={'/dashboard'} />,
  },
  // {
  //   path: '/home',
  //   element: <Navigate to={'/dashboard'} />,
  // },
  // route not found
  {
    element: <Error404Page />,
  },
];

export default commonRoutes;
